import { useState } from "react";
import { useParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useToast } from "@/components/ui/use-toast";
import { useTranslation } from "react-i18next"; // Import useTranslation hook

interface ForgotPasswordDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onBackToLogin: () => void;
}

const ForgotPasswordDialog = ({ isOpen, onClose, onBackToLogin }: ForgotPasswordDialogProps) => {
  const { t } = useTranslation();
  const { toast } = useToast();
  const { studioName } = useParams();
  const [email, setEmail] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    try {
      // In a real app, this would make an API call to handle password reset
      console.log("Forgot Password requested for:", email);
      
      const response = await fetch(`${import.meta.env.VITE_CLIENT_FORGOT_PASSWORD_ENDPOINT}`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ email, studioName }),
      });

      if (!response.ok) {
        throw new Error("Invalid email or password");
      }

      const data = await response.json();

      toast({
        title: t("forgotPassword.toastSuccessTitle"),
        description: t("forgotPassword.toastSuccessDescription"),
        className: "bg-green-500 text-white border-green-600",
        duration: 5000,
      });
      
      onClose();
    } catch (error) {
      toast({
        title: t("forgotPassword.toastErrorTitle"),
        description: t("forgotPassword.toastErrorDescription"),
        variant: "destructive",
        duration: 5000,
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[425px]">
        <DialogHeader>
          <DialogTitle>{t("forgotPassword.title")}</DialogTitle>
          <DialogDescription>
            {t("forgotPassword.description")}
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4 mt-4">
          <div className="space-y-2">
            <Label htmlFor="email">{t("forgotPassword.emailLabel")}</Label>
            <Input
              id="email"
              type="email"
              placeholder={t("forgotPassword.emailPlaceholder")}
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
            />
          </div>
          <div className="flex flex-col space-y-4">
            <Button type="submit" disabled={isSubmitting}>
              {isSubmitting ? t("forgotPassword.sendingButton") : t("forgotPassword.sendResetLinkButton")}
            </Button>
            <Button
              type="button"
              variant="outline"
              onClick={onBackToLogin}
            >
              {t("forgotPassword.backToLoginButton")}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default ForgotPasswordDialog;
